import { useQuery, useMutation } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { Tables } from "@/integrations/supabase/types"
import { useToast } from "@/hooks/use-toast"
import { queryClient } from "@/lib/react-query"

type Poll = Tables<"polls">
type PollOption = Tables<"poll_options">
type PollVote = Tables<"poll_votes">

export const usePolls = (meetingId?: string) => {
  const { profile } = useAuth()
  const { toast } = useToast()

  const query = useQuery({
    queryKey: ["polls", meetingId],
    queryFn: async () => {
      if (!meetingId) return []

      const { data, error } = await supabase
        .from("polls")
        .select(`
          *,
          poll_options (*),
          poll_votes (*)
        `)
        .eq("meeting_id", meetingId)
        .order("created_at", { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!meetingId
  })

  const createPoll = useMutation({
    mutationFn: async (pollData: {
      question: string
      description?: string
      poll_type: string
      options: string[]
      is_anonymous?: boolean
      allow_abstention?: boolean
    }) => {
      if (!profile?.id || !meetingId) throw new Error("Dados insuficientes")

      // Create poll
      const { data: poll, error: pollError } = await supabase
        .from("polls")
        .insert({
          meeting_id: meetingId,
          question: pollData.question,
          description: pollData.description,
          poll_type: pollData.poll_type,
          is_anonymous: pollData.is_anonymous || false,
          allow_abstention: pollData.allow_abstention || true,
          created_by: profile.id
        })
        .select()
        .single()

      if (pollError) throw pollError

      // Create poll options
      const optionsData = pollData.options.map((option, index) => ({
        poll_id: poll.id,
        option_text: option,
        option_order: index
      }))

      const { error: optionsError } = await supabase
        .from("poll_options")
        .insert(optionsData)

      if (optionsError) throw optionsError

      return poll
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["polls", meetingId] })
      toast({
        title: "Enquete criada",
        description: "A enquete foi criada com sucesso!"
      })
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar enquete",
        description: error.message,
        variant: "destructive"
      })
    }
  })

  const updatePollStatus = useMutation({
    mutationFn: async ({ pollId, status }: { pollId: string, status: string }) => {
      const { data, error } = await supabase
        .from("polls")
        .update({ status })
        .eq("id", pollId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["polls", meetingId] })
    }
  })

  const vote = useMutation({
    mutationFn: async ({ pollId, optionIds, voteType = "option" }: { 
      pollId: string
      optionIds?: string[]
      voteType?: string 
    }) => {
      if (!profile?.id) throw new Error("Usuário não autenticado")

      // Remove existing votes
      await supabase
        .from("poll_votes")
        .delete()
        .eq("poll_id", pollId)
        .eq("voter_id", profile.id)

      // Add new votes
      if (voteType === "abstention") {
        const { error } = await supabase
          .from("poll_votes")
          .insert({
            poll_id: pollId,
            voter_id: profile.id,
            vote_type: "abstention"
          })
        if (error) throw error
      } else if (optionIds && optionIds.length > 0) {
        const votes = optionIds.map(optionId => ({
          poll_id: pollId,
          option_id: optionId,
          voter_id: profile.id,
          vote_type: "option"
        }))

        const { error } = await supabase
          .from("poll_votes")
          .insert(votes)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["polls", meetingId] })
      toast({
        title: "Voto registrado",
        description: "Seu voto foi registrado com sucesso!"
      })
    },
    onError: (error) => {
      toast({
        title: "Erro ao votar",
        description: error.message,
        variant: "destructive"
      })
    }
  })

  return {
    polls: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    createPoll: createPoll.mutate,
    updatePollStatus: updatePollStatus.mutate,
    vote: vote.mutate,
    isCreating: createPoll.isPending,
    isVoting: vote.isPending
  }
}

export const usePollResults = (pollId: string) => {
  return useQuery({
    queryKey: ["poll-results", pollId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("polls")
        .select(`
          *,
          poll_options (
            *,
            votes:poll_votes!poll_votes_option_id_fkey (*)
          ),
          poll_votes (*)
        `)
        .eq("id", pollId)
        .single()

      if (error) throw error

      // Calculate results
      const totalVotes = data.poll_votes?.length || 0
      const optionVotes = data.poll_options?.map(option => ({
        ...option,
        vote_count: option.votes?.length || 0,
        percentage: totalVotes > 0 ? ((option.votes?.length || 0) / totalVotes) * 100 : 0
      })) || []

      const abstentions = data.poll_votes?.filter(vote => vote.vote_type === "abstention").length || 0

      return {
        ...data,
        results: {
          total_votes: totalVotes,
          option_votes: optionVotes,
          abstentions,
          abstention_percentage: totalVotes > 0 ? (abstentions / totalVotes) * 100 : 0
        }
      }
    },
    enabled: !!pollId,
    refetchInterval: 5000 // Refresh every 5 seconds for live results
  })
}